/**
 * Embedded WebView return-state scenes.
 *
 * Add a scene here when a list-like page enters a configured target route and
 * must restore its content and scroll position after native back navigation.
 */
export const WEBVIEW_RETURN_SCENES = Object.freeze({
  STORE_HOME: Object.freeze({
    id: 'store-home',
    stateKey: 'store-home-return-state',
    storageKey: 'store-home-return-state',
    returnPath: '/store',
    targetPathPatterns: [
      /^\/store\/products\/?$/,
      /^\/store\/products\/[^/]+\/?$/,
      /^\/store\/activity\/?$/,
    ],
    linkSelector: 'a[href]',
    restoreDelay: 300,
  }),
  PRODUCT_LIST: Object.freeze({
    id: 'store-product-list',
    stateKey: 'store-product-list-value-filter-return-state',
    storageKey: 'store-product-list-value-filter-return-state',
    returnPath: '/store/products',
    targetPathPatterns: [/^\/store\/products\/[^/]+\/?$/],
    linkSelector: 'a[target="_blank"]',
    restoreDelay: 300,
  }),
})

export const WEBVIEW_RETURN_SCENE_LIST = Object.freeze(Object.values(WEBVIEW_RETURN_SCENES))

/**
 * @description Check whether a route belongs to a configured WebView target.
 * @param {Object} scene - WebView return scene.
 * @param {string} routePath - Application-relative route path.
 * @returns {boolean} Whether the route should preserve the source page.
 */
export const matchesWebviewReturnTarget = (scene, routePath) => {
  return (scene?.targetPathPatterns || []).some((pattern) => pattern.test(routePath))
}

/**
 * @description Remove the Nuxt deployment base from an absolute URL pathname.
 * @param {string} pathname - Browser URL pathname.
 * @param {string} appBase - Nuxt application base, for example /tenda/.
 * @returns {string} Application-relative route path.
 */
export const normalizeWebviewRoutePath = (pathname, appBase = '/') => {
  const normalizedPath = String(pathname || '/').startsWith('/')
    ? String(pathname || '/')
    : `/${pathname}`
  const normalizedBase = `/${String(appBase || '/').replace(/^\/+|\/+$/g, '')}`

  if (normalizedBase === '/') return normalizedPath
  if (normalizedPath === normalizedBase) return '/'
  if (normalizedPath.startsWith(`${normalizedBase}/`)) {
    return normalizedPath.slice(normalizedBase.length) || '/'
  }
  return normalizedPath
}
